import { useEffect, useLayoutEffect, useState } from 'react'

import { useWidgetBreakpointSettings } from '@/hooks/useWidgetBreakpointSettings'
import { useWidgetStore } from '@/stores/useWidgetStore'
import { resolveIconPadding } from '@/utils/widget-utils'

const EDGE_PX = 8
const CONTENT_GAP = 8
/** Extra room required on the other side before flipping back (prevents flicker). */
const HYSTERESIS_PX = 32

export type OverlayContentLayout = {
  /** True when welcome/loop sit above the icon. */
  contentAbove: boolean
  /** True when welcome/loop sit to the right of the icon. */
  contentOnRight: boolean
  /** Icon-corner position used for welcome bubble radii (e.g. bottom-left). */
  effectivePosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  iconWidth: number
  iconHeight: number
}

type ContentSize = { width: number; height: number }

function pickSide(prev: boolean, needed: number, spaceWhenTrue: number, spaceWhenFalse: number): boolean {
  if (needed <= 0) return prev

  const spaceCurrent = prev ? spaceWhenTrue : spaceWhenFalse
  const spaceOther = prev ? spaceWhenFalse : spaceWhenTrue

  // Current side still fits — stay put
  if (needed <= spaceCurrent) return prev

  // Overflowing — only flip if the other side is clearly better
  if (spaceOther > spaceCurrent + HYSTERESIS_PX) return !prev

  return prev
}

/**
 * Picks which side welcome/loop content should sit on relative to the icon.
 * Starts from the configured icon corner, then flips horizontally and/or
 * vertically when the natural side would overflow after a drag.
 * Uses sticky state + hysteresis so the side doesn't flicker at the boundary.
 */
export function useOverlayContentLayout(
  contentSize: ContentSize = { width: 0, height: 0 }
): OverlayContentLayout {
  const overlayPosition = useWidgetStore((s) => s.overlayPosition)
  const { iconSettings } = useWidgetBreakpointSettings()
  const [viewport, setViewport] = useState(() => ({
    w: typeof window === 'undefined' ? 1024 : window.innerWidth,
    h: typeof window === 'undefined' ? 768 : window.innerHeight,
  }))

  const position = iconSettings?.position ?? 'bottom-left'
  const configuredTop = position.startsWith('top')
  const configuredRight = position.includes('right')
  const naturalContentOnRight = !configuredRight
  const naturalContentAbove = !configuredTop

  const [contentOnRight, setContentOnRight] = useState(naturalContentOnRight)
  const [contentAbove, setContentAbove] = useState(naturalContentAbove)

  useEffect(() => {
    setContentOnRight(naturalContentOnRight)
    setContentAbove(naturalContentAbove)
  }, [naturalContentOnRight, naturalContentAbove])

  useLayoutEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const iconWidth = iconSettings?.width ?? 48
  const iconHeight = iconSettings?.height ?? 48
  const padding = resolveIconPadding(iconSettings?.padding)

  const iconLeft = configuredRight
    ? viewport.w - padding.right - iconWidth + overlayPosition.x
    : padding.left + overlayPosition.x
  const iconTop = configuredTop
    ? padding.top + overlayPosition.y
    : viewport.h - padding.bottom - iconHeight + overlayPosition.y

  const spaceRight = viewport.w - (iconLeft + iconWidth) - EDGE_PX
  const spaceLeft = iconLeft - EDGE_PX
  const spaceBelow = viewport.h - (iconTop + iconHeight) - EDGE_PX
  const spaceAbove = iconTop - EDGE_PX

  const neededWidth = contentSize.width
  const neededHeight = contentSize.height > 0 ? contentSize.height + CONTENT_GAP : 0

  useLayoutEffect(() => {
    setContentOnRight((prev) => pickSide(prev, neededWidth, spaceRight, spaceLeft))
    setContentAbove((prev) => pickSide(prev, neededHeight, spaceAbove, spaceBelow))
  }, [neededWidth, neededHeight, spaceRight, spaceLeft, spaceAbove, spaceBelow])

  const effectivePosition = (
    contentAbove
      ? contentOnRight
        ? 'bottom-left'
        : 'bottom-right'
      : contentOnRight
        ? 'top-left'
        : 'top-right'
  ) as OverlayContentLayout['effectivePosition']

  return {
    contentAbove,
    contentOnRight,
    effectivePosition,
    iconWidth,
    iconHeight,
  }
}
